import { useCallback, useEffect, useRef, useState } from "react";
import { FiPhoneCall, FiPhoneOff } from "react-icons/fi";
import axios from "../api/axiosInstance";
import {
  normalizeCallableNumber,
  requestZadarmaHangup,
  zadarmaTelephonyEvents,
} from "./telephony/zadarmaTelephony";

const WIDGET_STATE_KEY = "__pilcoreZadarmaWebRtcWidget";
const HIDDEN_STYLE_ID = "pilcore-zadarma-hidden-widget-style";
const RUNTIME_TIMEOUT_MS = 15000;

const DEFAULT_TELEPHONY_STATE = {
  enabled: false,
  ready: false,
  active: false,
  status: "idle",
  message: "",
  error: "",
  call: null,
};

function getWidgetState() {
  if (typeof window === "undefined") return null;
  if (!window[WIDGET_STATE_KEY]) {
    window[WIDGET_STATE_KEY] = {
      signature: null,
      pendingSignature: null,
    };
  }
  return window[WIDGET_STATE_KEY];
}

function findScript(src) {
  if (typeof document === "undefined") return null;
  return Array.from(document.scripts || []).find(
    (script) => script.getAttribute("src") === src || script.src === src
  );
}

function loadScriptOnce(src) {
  const existing = findScript(src);
  if (existing?.dataset?.loaded === "true") return Promise.resolve();
  if (existing && existing.dataset.loading !== "true" && !existing.__zadarmaLoadPromise) {
    return Promise.resolve();
  }
  if (existing?.dataset?.loading === "true" && existing.__zadarmaLoadPromise) {
    return existing.__zadarmaLoadPromise;
  }

  const script = existing || document.createElement("script");
  script.src = src;
  script.async = true;
  script.dataset.loading = "true";
  script.dataset.pilcoreZadarmaWidget = "true";

  script.__zadarmaLoadPromise = new Promise((resolve, reject) => {
    script.addEventListener(
      "load",
      () => {
        script.dataset.loaded = "true";
        script.dataset.loading = "false";
        resolve();
      },
      { once: true }
    );
    script.addEventListener(
      "error",
      () => {
        script.dataset.loading = "false";
        reject(new Error(`Could not load Zadarma widget script: ${src}`));
      },
      { once: true }
    );
  });

  if (!existing) {
    document.body.appendChild(script);
  }

  return script.__zadarmaLoadPromise;
}

function installHiddenWidgetStyle() {
  if (typeof document === "undefined" || document.getElementById(HIDDEN_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = HIDDEN_STYLE_ID;
  style.textContent = `
    body:not(.pilcore-zadarma-debug-widget) #zdrmWPhI {
      position: fixed !important;
      left: auto !important;
      right: -10000px !important;
      bottom: 0 !important;
      width: 1px !important;
      height: 1px !important;
      overflow: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
}

function syncDebugWidgetClass() {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const debugVisible = window.localStorage.getItem("pilcore_zadarma_debug_widget") === "true";
  document.body.classList.toggle("pilcore-zadarma-debug-widget", debugVisible);
}

function waitForZadarmaRuntime(timeoutMs = RUNTIME_TIMEOUT_MS) {
  const startedAt = Date.now();

  return new Promise((resolve, reject) => {
    const check = () => {
      if (window.zdrmWPhI?.callNum && window.zdrmWPhI?.finishCall && window.zdrmWPhI?.apiWidget?.call) {
        resolve(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("Zadarma widget runtime did not become ready"));
        return;
      }

      window.setTimeout(check, 150);
    };

    check();
  });
}

async function initializeZadarmaWidget(config, businessUnitId) {
  const state = getWidgetState();
  if (!state || !config?.enabled || !config?.key || !config?.sip) return;

  const signature = `${businessUnitId || "active"}:${config.provider || "zadarma"}:${config.sip}`;
  if (state.signature === signature || state.pendingSignature === signature) return;

  state.pendingSignature = signature;

  try {
    const scripts = Array.isArray(config.scripts) ? config.scripts : [];
    for (const src of scripts) {
      await loadScriptOnce(src);
    }

    if (typeof window.zadarmaWidgetFn !== "function") {
      throw new Error("Zadarma widget function is not available after loading scripts");
    }

    const widget = config.widget || {};
    window.zadarmaWidgetFn(
      config.key,
      config.sip,
      widget.shape || "square",
      widget.language || "es",
      true,
      widget.position || "{right:'10px',bottom:'5px'}"
    );

    state.signature = signature;
  } finally {
    state.pendingSignature = null;
  }
}

function getRuntimeCallState(currentState) {
  if (typeof window === "undefined") return currentState;

  const interfaceStatus = String(window.zdrmWPhI?.status || "wait");
  const apiCallState = String(window.zdrmWebPhone?.callState || "");
  const widgetNumber = String(window.zdrmWPhI?.number || window.zdrmWebPhone?.callTo || "").trim();
  const isWidgetActive =
    Boolean(apiCallState && apiCallState !== "canceling") ||
    Boolean(interfaceStatus && interfaceStatus !== "wait");

  if (!isWidgetActive) {
    return {
      ...currentState,
      active: false,
      status: currentState.ready ? "ready" : currentState.status,
      call: null,
    };
  }

  let status = currentState.status || "calling";
  if (apiCallState === "talking") {
    status = "talking";
  } else if (apiCallState === "incoming" || interfaceStatus === "incoming") {
    status = "incoming";
  } else if (apiCallState === "outgoing" || interfaceStatus === "call") {
    status = "calling";
  }

  return {
    ...currentState,
    active: true,
    status,
    call: currentState.call || {
      number: widgetNumber,
      rawNumber: widgetNumber,
      label: widgetNumber || "Zadarma",
      entityType: null,
      entityId: null,
    },
  };
}

function getCallStatusText(status) {
  if (status === "talking") return "En llamada";
  if (status === "incoming") return "Llamada entrante";
  if (status === "ending") return "Finalizando llamada";
  return "Llamando";
}

export default function ZadarmaWebRtcWidget({ businessUnitId }) {
  const stateRef = useRef(DEFAULT_TELEPHONY_STATE);
  const [telephonyState, setTelephonyState] = useState(DEFAULT_TELEPHONY_STATE);

  const publishTelephonyState = useCallback((patch) => {
    setTelephonyState((previous) => {
      const next = {
        ...previous,
        ...patch,
        call: Object.prototype.hasOwnProperty.call(patch, "call") ? patch.call : previous.call,
      };

      stateRef.current = next;
      if (typeof window !== "undefined") {
        window.__pilcoreZadarmaTelephonyState = next;
        window.dispatchEvent(new CustomEvent(zadarmaTelephonyEvents.STATE_EVENT, { detail: next }));
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    installHiddenWidgetStyle();
    syncDebugWidgetClass();
    publishTelephonyState(DEFAULT_TELEPHONY_STATE);

    const loadConfig = async () => {
      try {
        const response = await axios.get("/api/crm/telephony/zadarma/webrtc-widget");
        if (cancelled) return;

        const config = response.data || {};
        if (!config.enabled) {
          publishTelephonyState({
            enabled: false,
            ready: false,
            active: false,
            status: "disabled",
            error: config.reason || "",
            call: null,
          });
          if (
            config.reason &&
            !["integration_not_active", "widget_disabled", "sip_extension_missing"].includes(config.reason)
          ) {
            console.info("[CRM][ZadarmaWebRTC] Widget not initialized:", config.reason);
          }
          return;
        }

        await initializeZadarmaWidget(config, businessUnitId);
        await waitForZadarmaRuntime();
        if (cancelled) return;
        publishTelephonyState({
          enabled: true,
          ready: true,
          active: false,
          status: "ready",
          error: "",
          call: null,
        });
      } catch (error) {
        if (!cancelled) {
          console.warn("[CRM][ZadarmaWebRTC] Widget initialization failed:", error);
          publishTelephonyState({
            enabled: false,
            ready: false,
            active: false,
            status: "error",
            error: error?.message || "Zadarma WebRTC widget failed to initialize",
            call: null,
          });
        }
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId, publishTelephonyState]);

  useEffect(() => {
    const call = (payload = {}) => {
      const number = normalizeCallableNumber(payload.number || payload.rawNumber);
      const current = stateRef.current;

      if (!current.ready) {
        publishTelephonyState({
          error: "Zadarma WebRTC no esta listo.",
          message: "Telefonia no disponible",
        });
        return;
      }
      if (current.active) {
        publishTelephonyState({
          message: "Ya hay una llamada activa",
        });
        return;
      }
      if (!number) {
        publishTelephonyState({
          error: "El contacto no tiene un telefono valido.",
          message: "Telefono no valido",
        });
        return;
      }

      try {
        const input = document.getElementById("zdrm-webphone-phonenumber-input");
        if (input) {
          input.removeAttribute("disabled");
          input.value = number;
        }

        publishTelephonyState({
          active: true,
          status: "calling",
          message: "",
          error: "",
          call: {
            number,
            rawNumber: payload.rawNumber || payload.number || number,
            label: payload.label || number,
            entityType: payload.entityType || null,
            entityId: payload.entityId || null,
            startedAt: new Date().toISOString(),
          },
        });

        if (window.zdrmWPhI?.callNum) {
          window.zdrmWPhI.callNum();
        } else if (window.zdrmWPhI?.apiWidget?.call) {
          window.zdrmWPhI.status = "call";
          window.zdrmWPhI.apiWidget.call(number);
        } else {
          throw new Error("Zadarma call API is not available");
        }
      } catch (error) {
        console.warn("[CRM][ZadarmaWebRTC] Direct call failed:", error);
        publishTelephonyState({
          active: false,
          status: "ready",
          error: error?.message || "No se pudo iniciar la llamada",
          call: null,
        });
      }
    };

    const hangup = () => {
      if (!stateRef.current.active) return;
      publishTelephonyState({ status: "ending", message: "", error: "" });
      try {
        if (window.zdrmWPhI?.finishCall) {
          window.zdrmWPhI.finishCall();
        } else if (window.zdrmWPhI?.apiWidget?.finishCall) {
          window.zdrmWPhI.apiWidget.finishCall();
        }
      } catch (error) {
        console.warn("[CRM][ZadarmaWebRTC] Hangup failed:", error);
      } finally {
        window.setTimeout(() => {
          publishTelephonyState({
            active: false,
            status: stateRef.current.ready ? "ready" : "idle",
            call: null,
          });
        }, 250);
      }
    };

    window.PilcoreZadarmaTelephony = {
      call,
      hangup,
      getState: () => stateRef.current,
      showDebugWidget: () => {
        window.localStorage.setItem("pilcore_zadarma_debug_widget", "true");
        syncDebugWidgetClass();
      },
      hideDebugWidget: () => {
        window.localStorage.removeItem("pilcore_zadarma_debug_widget");
        syncDebugWidgetClass();
      },
    };

    const handleCallRequest = (event) => call(event.detail || {});
    const handleHangupRequest = () => hangup();

    window.addEventListener(zadarmaTelephonyEvents.CALL_REQUEST_EVENT, handleCallRequest);
    window.addEventListener(zadarmaTelephonyEvents.HANGUP_REQUEST_EVENT, handleHangupRequest);

    return () => {
      window.removeEventListener(zadarmaTelephonyEvents.CALL_REQUEST_EVENT, handleCallRequest);
      window.removeEventListener(zadarmaTelephonyEvents.HANGUP_REQUEST_EVENT, handleHangupRequest);
    };
  }, [publishTelephonyState]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const current = stateRef.current;
      if (!current.ready && !current.active) return;
      const next = getRuntimeCallState(current);
      if (
        next.active !== current.active ||
        next.status !== current.status ||
        next.call?.number !== current.call?.number
      ) {
        publishTelephonyState(next);
      }
    }, 600);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [publishTelephonyState]);

  if (!telephonyState.active || !telephonyState.call) {
    return null;
  }

  const statusText = getCallStatusText(telephonyState.status);
  const callLabel = telephonyState.call.label || telephonyState.call.rawNumber || telephonyState.call.number;

  return (
    <div className="crm-zadarmaCallBar" role="status" aria-live="polite">
      <div className="crm-zadarmaCallIcon" aria-hidden="true">
        <FiPhoneCall />
      </div>
      <div className="crm-zadarmaCallMain">
        <strong>
          {statusText} a {callLabel}
        </strong>
        <span>{telephonyState.call.number}</span>
      </div>
      <button
        type="button"
        className="crm-zadarmaHangup"
        onClick={requestZadarmaHangup}
        title="Colgar"
        aria-label="Colgar"
      >
        <FiPhoneOff aria-hidden="true" />
      </button>
    </div>
  );
}
